import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useGameStore } from '../store/gameStore';
import { generateNetworkLayout, calculateNodeColor, TEAM_COLORS, SECTOR_COLORS } from '../utils/networkVisualization';
import { Population } from '../types/iloe';
import { Community } from '../types/community';
import { motion } from 'framer-motion';
import { Tooltip } from './Tooltip';

interface PopulationNetworkProps {
  width?: number;
  height?: number;
  onNodeSelect?: (id: string, type: 'population' | 'community') => void;
  selectedId?: string | null;
}

export const PopulationNetwork: React.FC<PopulationNetworkProps> = ({ 
  width = 800, 
  height = 600,
  onNodeSelect,
  selectedId
}) => {
  const sectors = useGameStore(state => state.sectors);
  const networkGraph = useGameStore(state => state.getNetworkGraph());
  const [populations, setPopulations] = useState<Population[]>([]);
  
  useEffect(() => {
    import('../data/populations').then(mod => {
      const allPops = mod.POPULATIONS.map(p => {
        const current = networkGraph.getPopulation(p.id);
        return current || p;
      });
      setPopulations(allPops);
    });
  }, [useGameStore(state => state.turn), useGameStore(state => state.events)]);

  const [communities, setCommunities] = useState<Community[]>([]);
  useEffect(() => {
    import('../data/communities').then(mod => {
      setCommunities(mod.COMMUNITIES);
    });
  }, []);

  // Layout
  const layout = useMemo(() => {
    if (communities.length === 0 || populations.length === 0) return null;
    return generateNetworkLayout(communities, populations, 2000, 2000); // Large virtual canvas
  }, [communities, populations]);

  // Viewport State
  const [view, setView] = useState({ x: -600, y: -600, k: 0.5 }); // Start zoomed out
  const [isDragging, setIsDragging] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [showPopulations, setShowPopulations] = useState(true);

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const scaleBy = 1.1;
    const newK = e.deltaY < 0 ? view.k * scaleBy : view.k / scaleBy;
    // Clamp zoom
    const clampedK = Math.min(Math.max(0.1, newK), 4);
    setView(v => ({ ...v, k: clampedK }));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setLastPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      const dx = e.clientX - lastPos.x;
      const dy = e.clientY - lastPos.y;
      setView(v => ({ ...v, x: v.x + dx, y: v.y + dy }));
      setLastPos({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  if (!layout) return <div className="text-white">Loading Network...</div>;

  return (
    <div className="relative w-full h-full bg-gray-900 overflow-hidden">
      <svg 
        ref={svgRef}
        width="100%" 
        height="100%"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        className="cursor-move"
      >
        <g transform={`translate(${view.x + width/2}, ${view.y + height/2}) scale(${view.k})`}>
          {/* Connections */}
          {showPopulations && populations.map(pop => (
            pop.networkedToPopulations.map(targetId => {
              const start = layout.nodes[pop.id];
              const end = layout.nodes[targetId];
              if (!start || !end) return null;
              // Only draw one way to avoid duplicates (id check)
              if (pop.id > targetId) return null; 
              
              return (
                <line
                  key={`${pop.id}-${targetId}`}
                  x1={start.x}
                  y1={start.y}
                  x2={end.x}
                  y2={end.y}
                  stroke="#4b5563"
                  strokeWidth={1}
                  opacity={0.3}
                />
              );
            })
          ))}

          {/* Community Clusters (Backgrounds) */}
          {communities.map(comm => {
            const pos = layout.nodes[comm.id];
            if (!pos) return null;
            const isSelected = selectedId === comm.id;
            
            return (
              <g
                key={`comm-${comm.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onNodeSelect?.(comm.id, 'community');
                }}
                className="cursor-pointer"
              >
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={60}
                  fill={SECTOR_COLORS[comm.sectorId] || '#374151'}
                  opacity={0.2}
                  stroke={isSelected ? '#fff' : 'none'}
                  strokeWidth={2}
                />
                {/* Community Label */}
                {(!showPopulations || view.k < 0.5) && (
                  <text
                    x={pos.x}
                    y={pos.y}
                    textAnchor="middle"
                    fill="white"
                    fontSize={14}
                    pointerEvents="none"
                    style={{ textShadow: '0 1px 2px black' }}
                  >
                    {comm.name}
                  </text>
                )}
              </g>
            );
          })}

          {/* Population Nodes */}
          {showPopulations && populations.map(pop => {
            const pos = layout.nodes[pop.id];
            if (!pos) return null;
            const isSelected = selectedId === pop.id;
            const isHovered = hoveredNode === pop.id;
            
            return (
              <g 
                key={pop.id} 
                transform={`translate(${pos.x}, ${pos.y})`}
                onClick={(e) => {
                  e.stopPropagation();
                  onNodeSelect?.(pop.id, 'population');
                }}
                onMouseEnter={() => setHoveredNode(pop.id)}
                onMouseLeave={() => setHoveredNode(null)}
                className="cursor-pointer"
              >
                <circle
                  r={isHovered ? 8 : 5}
                  fill={calculateNodeColor(pop.belief, pop.behavior)}
                  stroke={isSelected ? '#fff' : '#000'}
                  strokeWidth={isSelected ? 2 : 1}
                />
                {/* Label on hover or zoom level high */}
                {(isHovered || view.k > 1.5) && (
                  <text
                    y={-10}
                    textAnchor="middle"
                    fill="white"
                    fontSize={10 / view.k + 4} // Scale text inversely to zoom so it stays readable
                    pointerEvents="none"
                    style={{ textShadow: '0 1px 2px black' }}
                  >
                    {pop.name}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>
      
      {/* Overlay Controls */}
      <div className="absolute bottom-4 right-4 flex gap-2">
        <button 
          className="bg-gray-800 text-white p-2 rounded hover:bg-gray-700 text-xs font-bold"
          onClick={() => setShowPopulations(!showPopulations)}
        >
          {showPopulations ? 'Strategic View' : 'Detailed View'}
        </button>
        <button 
          className="bg-gray-800 text-white p-2 rounded hover:bg-gray-700"
          onClick={() => setView(v => ({ ...v, k: v.k * 1.2 }))}
        >
          +
        </button>
        <button 
          className="bg-gray-800 text-white p-2 rounded hover:bg-gray-700"
          onClick={() => setView(v => ({ ...v, k: v.k / 1.2 }))}
        >
          -
        </button>
        <button 
          className="bg-gray-800 text-white p-2 rounded hover:bg-gray-700"
          onClick={() => setView({ x: -600, y: -600, k: 0.5 })}
        >
          Reset
        </button>
      </div>
    </div>
  );
};
